import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { inputSlotClasses, wrapperClasses } from './InputOTP.styles';
import type { InputOTPProps, InputSlotProps } from './InputOTP.types';
import { extractValidChars, isValidChar } from './utils';

const InputSlot = forwardRef<HTMLInputElement, InputSlotProps>(
  (
    { className, value, onChange, onKeyDown, onPaste, validationStatus, inputMode, ...props },
    ref
  ) => (
    <input
      ref={ref}
      className={cn(inputSlotClasses({ validationStatus }), className)}
      name="otp"
      autoComplete="one-time-code"
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      inputMode={inputMode}
      aria-label="OTP Digit/Character"
      {...props}
    />
  )
);

export const InputOTP: React.FC<InputOTPProps> = ({
  className,
  length,
  value = '',
  onValueChange,
  isValid,
  inputMode = 'numeric'
}) => {
  const [otpValues, setOtpValues] = useState<string[]>(() =>
    Array.from({ length }, (_, i) => value[i] ?? '')
  );
  const [lastSynced, setLastSynced] = useState({ value, length });
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const otpValuesRef = useRef(otpValues);

  // Keep ref in sync with state (read only in event handlers/effects, not during render)
  useEffect(() => {
    otpValuesRef.current = otpValues;
  });

  // Sync from controlled value when props change (during render; avoids setState in effect)
  if (value !== lastSynced.value || length !== lastSynced.length) {
    setLastSynced({ value, length });
    setOtpValues(Array.from({ length }, (_, i) => value[i] ?? ''));
  }

  const isComplete = otpValues.every(Boolean);
  const validationStatus = isComplete ? isValid : undefined;

  const focus = useCallback((index: number) => {
    const el = inputRefs.current[index];
    el?.focus();
  }, []);

  const handleChange = useCallback(
    (index: number, inputValue: string) => {
      const char = (inputValue.slice(-1) || inputValue.slice(0, 1)).toUpperCase();
      if (!char || !isValidChar(char, inputMode)) {
        return;
      }
      const prev = otpValuesRef.current;
      const next = [...prev];
      next[index] = char;
      setOtpValues(next);
      onValueChange?.(next.join(''));
      if (index < length - 1) {
        focus(index + 1);
      }
    },
    [inputMode, length, onValueChange, focus]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Backspace') {
        return;
      }
      e.preventDefault();
      const prev = otpValuesRef.current;
      if (prev[index]) {
        // Current slot has a char: clear it and stay here
        const next = [...prev];
        next[index] = '';
        setOtpValues(next);
        onValueChange?.(next.join(''));
      } else if (index > 0) {
        // Current slot empty: clear previous and focus it
        const next = [...prev];
        next[index - 1] = '';
        setOtpValues(next);
        onValueChange?.(next.join(''));
        focus(index - 1);
      }
    },
    [onValueChange, focus]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text');
      const chars = extractValidChars(text, inputMode).map((c) => c.toUpperCase());
      if (chars.length === 0) {
        return;
      }

      const focusedIndex = inputRefs.current.findIndex((el) => el === document.activeElement);
      const start = focusedIndex >= 0 ? focusedIndex : 0;

      const prev = otpValuesRef.current;
      const next = [...prev];
      chars.forEach((char, i) => {
        const idx = start + i;
        if (idx < length && isValidChar(char, inputMode)) {
          next[idx] = char;
        }
      });
      setOtpValues(next);
      onValueChange?.(next.join(''));

      const nextFocus = Math.min(start + chars.length, length - 1);
      focus(nextFocus);
    },
    [inputMode, length, onValueChange, focus]
  );

  const setRef = useCallback(
    (index: number) => (el: HTMLInputElement | null) => {
      inputRefs.current[index] = el;
    },
    []
  );

  return (
    <div className={cn(wrapperClasses, className)}>
      {Array.from({ length }, (_, index) => (
        <InputSlot
          key={index}
          ref={setRef(index)}
          value={otpValues[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          validationStatus={validationStatus}
          inputMode={inputMode}
        />
      ))}
    </div>
  );
};
